import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import {
  isOrgAdmin,
  isOrgOwner,
  isProjectTypeAdmin,
  isProjectTypeMember,
  isSuperuser,
} from "@/core/lib/access";
import {
  AUDIT_PROJECT_TYPE,
  CORPORATE_FINANCE_PROJECT_TYPE,
} from "@/core/lib/constants";
import { DEPARTMENT_PROJECT_TYPES } from "@/core/lib/project-types";
import { denyRouteAccess } from "@/core/lib/route-access";
import {
  preloadPromotionReviewQueue,
  promotionReviewSearchSchema,
  requireOrganizationId,
} from "@/features/promotion/lib/promotion-review-route";

const templateEditorSearchSchema = promotionReviewSearchSchema.extend({
  category: z
    .enum(["document_analysis", "classifications", "statement_taxonomy"])
    .optional(),
  projectType: z.enum(DEPARTMENT_PROJECT_TYPES).optional(),
});

type TemplateEditorUser = Parameters<typeof isProjectTypeMember>[0];

export function canAccessTemplateEditorProjectType(
  user: TemplateEditorUser,
  projectType: string,
  hasExplicitProjectType: boolean,
): boolean {
  if (isProjectTypeMember(user, projectType)) return true;
  if (!hasExplicitProjectType) return false;
  return (
    isSuperuser(user) ||
    isOrgOwner(user) ||
    isOrgAdmin(user) ||
    isProjectTypeAdmin(user, projectType)
  );
}

export const Route = createFileRoute("/_protected/template-editor/")({
  validateSearch: templateEditorSearchSchema,
  beforeLoad: ({ context, search, ...guard }) => {
    const hasExplicitProjectType = search.projectType !== undefined;
    const projectType =
      search.projectType ??
      (isProjectTypeMember(context.user, AUDIT_PROJECT_TYPE)
        ? AUDIT_PROJECT_TYPE
        : isProjectTypeMember(context.user, CORPORATE_FINANCE_PROJECT_TYPE)
          ? CORPORATE_FINANCE_PROJECT_TYPE
          : null);

    if (
      !projectType ||
      !canAccessTemplateEditorProjectType(
        context.user,
        projectType,
        hasExplicitProjectType,
      )
    ) {
      denyRouteAccess(guard, "template_editor_access");
    }

    if (!search.projectType) {
      throw redirect({
        to: "/template-editor",
        search: { ...search, projectType },
        replace: true,
      });
    }
  },
  loader: ({ context: { queryClient, user } }) => {
    const orgId = requireOrganizationId(user.organization?.id);
    preloadPromotionReviewQueue(
      queryClient,
      orgId,
      "document_analysis_template",
    );
  },
});
